import { Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/occurrences/button'
import { CauseGraph } from '@/components/occurrences/cause-graph'
import {
	Field,
	JustificationDialog,
	Notice,
	Panel,
} from '@/components/occurrences/occurrence-ui'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ISHIKAWA_CATEGORIES } from '@/mocks/occurrences/catalog'
import { newId, useOccurrenceMock } from '@/mocks/occurrences/store'
import type { Cause, InvestigationMethod, Occurrence } from '@/types/occurrence'
import { METHOD_LABEL, STATUS_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

/** Recomendação por tipo, potencial e complexidade (regra de exemplo para o protótipo). */
const recommend = (o: Occurrence): InvestigationMethod => {
	if (
		['N1', 'N2'].includes(o.potential) ||
		o.classification === 'CAF' ||
		o.classification === 'FA'
	)
		return 'CAUSE_TREE'
	if (o.potential === 'N3') return 'ISHIKAWA'
	return 'FIVE_WHYS'
}

const METHOD_HELP: Record<InvestigationMethod, string> = {
	CAUSE_TREE:
		'Eventos graves ou complexos: parte do evento indesejado e desdobra fatos com relações E/OU.',
	FIVE_WHYS:
		'Eventos simples e lineares: sequência de “por quês” até a causa-raiz.',
	ISHIKAWA:
		'Eventos de complexidade média: agrupa causas por categoria (6M) e aprofunda as principais com 5 Porquês.',
}

// ─── Seção principal ────────────────────────────────────────────────────────
export const InvestigationSection = () => {
	const { occ, editable } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const recommended = recommend(occ)
	const inv = occ.investigation

	const [method, setMethod] = useState<InvestigationMethod | null>(
		inv.method ?? recommended,
	)
	const [causes, setCauses] = useState<Cause[]>(inv.causes)
	const [whys, setWhys] = useState<string[]>(
		inv.whys.length ? inv.whys : ['', '', '', '', ''],
	)
	const [participants, setParticipants] = useState<string[]>(inv.participants)
	const [changeReason, setChangeReason] = useState<string | null>(
		inv.methodChangeReason ?? null,
	)
	const [askReason, setAskReason] = useState<InvestigationMethod | null>(null)
	const [saved, setSaved] = useState(false)

	const readOnly =
		!editable ||
		(occ.status !== 'INVESTIGATING' &&
			occ.status !== 'REOPENED' &&
			occ.status !== 'AWAITING_VALIDATION')
	const canChooseMethod = !readOnly
	const treeRoot = causes.find(
		(c) => c.parentId === null && method === 'CAUSE_TREE',
	)

	const choose = (m: InvestigationMethod) => {
		if (m !== recommended && !changeReason) return setAskReason(m)
		setMethod(m)
		setSaved(false)
	}

	const addCause = (category: string) => {
		setSaved(false)
		setCauses((s) => [
			...s,
			{ id: newId(), text: '', category, parentId: null, isRoot: false },
		])
	}
	const patchCause = (id: string, p: Partial<Cause>) => {
		setSaved(false)
		setCauses((s) => s.map((c) => (c.id === id ? { ...c, ...p } : c)))
	}

	const ensureTreeRoot = () => {
		if (!causes.some((c) => c.parentId === null))
			setCauses([
				{
					id: newId(),
					text: occ.description,
					parentId: null,
					isRoot: false,
					logic: 'AND',
				},
			])
	}

	useEffect(() => {
		if (method === 'CAUSE_TREE' && !readOnly && !treeRoot) ensureTreeRoot()
	})

	const save = () => {
		update(
			occ.id,
			{
				investigation: {
					...inv,
					method,
					recommended,
					methodChangeReason: changeReason ?? undefined,
					participants,
					causes,
					whys: whys.filter((w) => w.trim()),
				},
			},
			'Investigação de causas atualizada',
			{
				detail: `${method ? METHOD_LABEL[method] : 'sem metodologia'} · ${causes.filter((c) => c.isRoot).length} causa(s)-raiz`,
			},
		)
		setSaved(true)
	}

	return (
		<div className="space-y-4">
			<Panel
				title="Metodologia de investigação"
				description="O sistema recomenda conforme tipo, potencial e complexidade. O Gestor SSMA pode alterar com justificativa."
			>
				<Notice>
					Recomendado para esta ocorrência ({occ.potential},{' '}
					{occ.classification ?? 'sem classificação'}):{' '}
					<b>{METHOD_LABEL[recommended]}</b>
				</Notice>
				<div className="grid grid-cols-1 gap-3 md:grid-cols-3">
					{(Object.keys(METHOD_LABEL) as InvestigationMethod[]).map((m) => (
						<button
							key={m}
							type="button"
							disabled={!canChooseMethod}
							onClick={() => choose(m)}
							className={cn(
								'rounded-xl border-2 p-3 text-left transition-all disabled:cursor-not-allowed',
								method === m
									? 'border-kaefer-dark-grey bg-muted/40'
									: 'border-transparent bg-muted/20 hover:bg-muted/40',
							)}
						>
							<p className="text-sm font-semibold text-kaefer-dark-grey">
								{METHOD_LABEL[m]}{' '}
								{m === recommended && (
									<span className="ml-1 rounded bg-kaefer-honey-yellow/30 px-1.5 text-[10px]">
										recomendada
									</span>
								)}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{METHOD_HELP[m]}
							</p>
						</button>
					))}
				</div>
				{changeReason && method !== recommended && (
					<p className="text-xs text-muted-foreground">
						Alteração da recomendação justificada: “{changeReason}”
					</p>
				)}
				{readOnly && (
					<Notice tone="warn">
						Edição indisponível: a investigação só é editável com a ocorrência
						“Em investigação” (ou aguardando validação) e perfil com permissão
						de edição. Status atual: {STATUS_LABEL[occ.status]}.
					</Notice>
				)}
				<Field label="Participantes">
					<div className="flex flex-wrap gap-1.5">
						{occ.committee.map((m) => {
							const on = participants.includes(m.name)
							return (
								<button
									type="button"
									key={m.id}
									disabled={readOnly}
									onClick={() => {
										setSaved(false)
										setParticipants((s) =>
											on ? s.filter((x) => x !== m.name) : [...s, m.name],
										)
									}}
									className={cn(
										'rounded-full border px-2.5 py-0.5 text-xs',
										on
											? 'border-kaefer-dark-grey bg-kaefer-dark-grey text-white'
											: 'bg-muted/30',
									)}
								>
									{m.name}
								</button>
							)
						})}
						{occ.committee.length === 0 && (
							<span className="text-xs text-muted-foreground">
								Defina o comitê primeiro.
							</span>
						)}
					</div>
				</Field>
			</Panel>

			{method === 'FIVE_WHYS' && (
				<Panel
					title="5 Porquês"
					description="Cada resposta alimenta o próximo “por quê”. A última resposta é a causa-raiz candidata."
				>
					<ol className="space-y-2">
						{whys.map((w, i) => (
							<li key={i} className="flex items-center gap-2">
								<span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-kaefer-dark-grey text-xs font-bold text-white">
									{i + 1}
								</span>
								<Input
									disabled={readOnly}
									value={w}
									placeholder={
										i === 0
											? 'Por que o evento ocorreu?'
											: 'Por que isso aconteceu?'
									}
									onChange={(e) => {
										setSaved(false)
										setWhys((s) =>
											s.map((x, j) => (j === i ? e.target.value : x)),
										)
									}}
								/>
							</li>
						))}
					</ol>
					{!readOnly && whys.length < 7 && (
						<Button
							size="sm"
							variant="outline"
							onClick={() => setWhys((s) => [...s, ''])}
						>
							+ Mais um porquê
						</Button>
					)}
					{!readOnly && (
						<Button
							size="sm"
							variant="secondary"
							disabled={!whys.filter((w) => w.trim()).length}
							onClick={() => {
								const last = whys.filter((w) => w.trim()).at(-1) ?? ''
								setCauses((s) =>
									s.some((c) => c.text === last)
										? s
										: [
												...s,
												{
													id: newId(),
													text: last,
													parentId: null,
													isRoot: true,
													category: 'Método',
												},
											],
								)
								setSaved(false)
							}}
						>
							Registrar última resposta como causa-raiz
						</Button>
					)}
				</Panel>
			)}

			{method === 'ISHIKAWA' && (
				<Panel
					title="Ishikawa (6M) + 5 Porquês"
					description="Agrupe as causas por categoria e marque as principais como causa-raiz; aprofunde-as com 5 Porquês."
				>
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
						{ISHIKAWA_CATEGORIES.map((cat) => (
							<div key={cat} className="rounded-lg border p-2">
								<div className="mb-2 flex items-center justify-between">
									<span className="text-sm font-semibold text-kaefer-dark-grey">
										{cat}
									</span>
									{!readOnly && (
										<Button
											size="icon"
											variant="ghost"
											className="size-6"
											aria-label={`Adicionar causa em ${cat}`}
											onClick={() => addCause(cat)}
										>
											<Plus className="size-4" />
										</Button>
									)}
								</div>
								<div className="space-y-1.5">
									{causes
										.filter((c) => c.category === cat && c.parentId === null)
										.map((c) => (
											<div
												key={c.id}
												className={cn(
													'flex items-center gap-1.5 rounded border p-1',
													c.isRoot &&
														'border-kaefer-grass-green bg-kaefer-grass-green/5',
												)}
											>
												<Input
													className="h-8 flex-1"
													disabled={readOnly}
													value={c.text}
													placeholder="Causa..."
													onChange={(e) =>
														patchCause(c.id, { text: e.target.value })
													}
												/>
												<label className="flex items-center gap-1 text-[11px]">
													<input
														type="checkbox"
														disabled={readOnly}
														checked={c.isRoot}
														onChange={(e) =>
															patchCause(c.id, { isRoot: e.target.checked })
														}
													/>
													raiz
												</label>
												{!readOnly && (
													<Button
														size="icon"
														variant="ghost"
														className="size-6"
														aria-label="Remover"
														onClick={() => {
															setSaved(false)
															setCauses((s) => s.filter((x) => x.id !== c.id))
														}}
													>
														<Trash2 className="size-3 text-kaefer-red" />
													</Button>
												)}
											</div>
										))}
								</div>
							</div>
						))}
					</div>
					<div className="space-y-2 rounded-lg border bg-muted/20 p-3">
						<p className="text-sm font-semibold text-kaefer-dark-grey">
							5 Porquês da causa principal
						</p>
						{whys.map((w, i) => (
							<Input
								key={i}
								disabled={readOnly}
								value={w}
								placeholder={`Porquê ${i + 1}`}
								onChange={(e) => {
									setSaved(false)
									setWhys((s) =>
										s.map((x, j) => (j === i ? e.target.value : x)),
									)
								}}
							/>
						))}
					</div>
				</Panel>
			)}

			{method === 'CAUSE_TREE' && (
				<Panel
					title="Árvore de Causas"
					description="Parta do evento e desdobre fatos. Use E/OU para a relação entre causas e marque as causas-raiz que originarão ações."
				>
					{!treeRoot && !readOnly && (
						<Button variant="outline" onClick={ensureTreeRoot}>
							Iniciar árvore a partir da descrição
						</Button>
					)}
					{treeRoot && (
						<CauseGraph
							all={causes}
							root={treeRoot}
							readOnly={readOnly}
							onChange={(n) => {
								setSaved(false)
								setCauses(n)
							}}
						/>
					)}
					<p className="text-xs text-muted-foreground">
						Use + para criar uma causa ligada à caixa, marque “Causa-raiz” nas
						causas finais e clique em E/OU para alternar a relação entre as
						filhas. As posições das caixas também são salvas. Depois clique em
						“Salvar investigação”.
					</p>
				</Panel>
			)}

			{method && (
				<Panel
					title="Causas-raiz identificadas"
					description="Estas causas ficam disponíveis para vincular ações no plano."
				>
					{causes.filter((c) => c.isRoot).length === 0 ? (
						<p className="text-sm text-muted-foreground">
							Nenhuma causa-raiz marcada.
						</p>
					) : (
						<ul className="list-disc space-y-1 pl-5 text-sm">
							{causes
								.filter((c) => c.isRoot)
								.map((c) => (
									<li key={c.id}>
										{c.text || <em>sem descrição</em>}{' '}
										{c.category && (
											<span className="text-xs text-muted-foreground">
												({c.category})
											</span>
										)}
									</li>
								))}
						</ul>
					)}
					{!readOnly && (
						<div className="flex items-center justify-end gap-3">
							{saved && (
								<span className="text-sm text-kaefer-forest-green">Salvo</span>
							)}
							<Button
								variant="primary"
								className="gap-2 font-semibold"
								disabled={!method}
								onClick={save}
							>
								<Save className="size-4" /> Salvar investigação
							</Button>
						</div>
					)}
				</Panel>
			)}

			<JustificationDialog
				open={askReason !== null}
				onOpenChange={(v) => !v && setAskReason(null)}
				title="Alterar metodologia recomendada"
				description={`Você está escolhendo ${askReason ? METHOD_LABEL[askReason] : ''} em vez de ${METHOD_LABEL[recommended]}.`}
				onConfirm={(j) => {
					setChangeReason(j)
					setMethod(askReason)
					setSaved(false)
				}}
			/>
		</div>
	)
}
